import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import type { Character } from '../data/cutscenes'
import { installTentacleSurface } from './DioramaTentacleSurface'
import { applyJellyMembrane, makeCharacterSurfaceMap, spongeMapChunk } from './CharacterSurfaceMaterials'

/** Cutscene-only finish of a cloned GLB. Template materials/geometry are never mutated
 * or disposed. Install before the actor captures its rest pose; release before the
 * model is removed or its fallback resources are disposed. The hook is idempotent. */
export function finishDioramaCharacter(model: THREE.Group, character: Character): () => void {
  const materialCopies = new Map<THREE.Material, THREE.Material>()
  const ownMaterials = new Set<THREE.Material>()
  const ownGeometries = new Set<THREE.BufferGeometry>()
  const undo: (() => void)[] = []
  const surfaceMaps = new Map<Parameters<typeof makeCharacterSurfaceMap>[0], THREE.DataTexture>()
  const surfaceMap = (kind: Parameters<typeof makeCharacterSurfaceMap>[0]) => {
    if (!surfaceMaps.has(kind)) surfaceMaps.set(kind, makeCharacterSurfaceMap(kind))
    return surfaceMaps.get(kind)!
  }
  let poreMap: THREE.DataTexture | null = null
  const poreTexture = () => poreMap ??= makePoreTexture()

  const materialFor = (source: THREE.Material): THREE.Material => {
    const prior = materialCopies.get(source)
    if (prior) return prior
    let material: THREE.Material = source.clone()
    if (source instanceof THREE.MeshStandardMaterial) {
      const name = source.name
      const wet = /^(ZP|JK)_(Bell|Tent|Rim|Spot)/.test(name) || /^MK_Red/.test(name)
      if (wet) {
        // Preserve standard fields through the Standard copy implementation, then
        // author Physical fields. Physical.copy expects a Physical source.
        material.dispose()
        material = new THREE.MeshPhysicalMaterial()
        THREE.MeshStandardMaterial.prototype.copy.call(material, source)
        const physical = material as THREE.MeshPhysicalMaterial
        physical.defines = { STANDARD: '', PHYSICAL: '' }
        physical.clearcoat = character === 'krabs' ? 0.48 : 0.42
        physical.clearcoatRoughness = character === 'krabs' ? 0.3 : 0.17
        physical.roughness = character === 'krabs' ? 0.38 : 0.31
        physical.metalness = 0
        physical.transmission = 0
        physical.ior = 1.38
        physical.iridescence = character === 'krabs' ? 0 : 0.18
        physical.iridescenceIOR = 1.3
        physical.sheen = character === 'krabs' ? 0 : 0.12
        physical.sheenColor.setHex(character === 'king' ? 0xa9b9ff : 0xffb8da)
        if (character === 'krabs') {
          physical.roughness = 0.62
          physical.roughnessMap = surfaceMap('shell-roughness')
          physical.clearcoat = 0.34
          physical.normalScale.multiplyScalar(0.6)
        } else if (/_(Bell|Bell2)$/.test(name)) {
          applyJellyMembrane(physical)
        } else {
          physical.roughness = 0.46
          physical.clearcoat = 0.2
          physical.normalMap = null
          if (/_Rim$/.test(name)) { applyJellyMembrane(physical); physical.thickness = 0.055; physical.color.setHex(character === 'king' ? 0xd5bce8 : 0xa6e4e4); physical.attenuationColor.setHex(character === 'king' ? 0x9256b0 : 0x249ba4) }
        }
      }
      const standard = material as THREE.MeshStandardMaterial
      if (name === 'BB_Sponge') {
        if (standard.map) standard.color.setRGB(1, 1, 1)
        else standard.color.setRGB(0.14, 0.52, 0.76)
        standard.roughness = 0.87
        standard.normalScale.multiplyScalar(0.55)
        standard.onBeforeCompile = shader => {
          shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', spongeMapChunk())
        }
        standard.customProgramCacheKey = () => 'sponge-balanced-pores-v1'
        standard.metalness = 0
        if (!standard.normalMap) {
          standard.bumpMap = poreTexture()
          standard.bumpScale = 0.028
        }
      } else if (/Shirt|Pants|Belt/.test(name) || name === 'BB_Red') {
        standard.roughness = 0.88
        standard.metalness = 0
        standard.map = surfaceMap('cloth-color')
        standard.normalMap = surfaceMap('cloth-normal')
        standard.normalScale.set(0.45, 0.45)
      } else if (/Gold/.test(name)) {
        standard.roughness = 0.31
        standard.roughnessMap = surfaceMap('shell-roughness')
        standard.metalness = 0.92
      } else if (/White/.test(name)) {
        standard.color.setRGB(0.91, 0.92, 0.86)
        standard.roughness = 0.24
      } else if (/Black/.test(name)) {
        standard.color.setRGB(0.016, 0.026, 0.038)
        standard.roughness = 0.23
      } else if (name === 'BB_Pore') {
        standard.color.setRGB(0.09, 0.34, 0.49)
        standard.roughness = 0.95
        standard.side = THREE.DoubleSide
      }
    }
    material.name = `${source.name}_CutsceneFinish`
    materialCopies.set(source, material)
    ownMaterials.add(material)
    return material
  }

  const replaceGeometry = (mesh: THREE.Mesh, geometry: THREE.BufferGeometry) => {
    const original = mesh.geometry
    mesh.geometry = geometry
    ownGeometries.add(geometry)
    undo.push(() => { mesh.geometry = original })
  }

  const meshes: THREE.Mesh[] = []
  model.traverse((part) => { if (part instanceof THREE.Mesh) meshes.push(part) })
  for (const mesh of meshes) {
    const original = mesh.material
    if (/RadialCanals/.test(mesh.name) || mesh.name.startsWith('ZP_Spot_')) {
      const visible = mesh.visible
      mesh.visible = false
      undo.push(() => { mesh.visible = visible })
    }
    if (/^ZP_(Spark|Ant)_/.test(mesh.name)) {
      const visible = mesh.visible
      mesh.visible = false
      const glow = makeSparkGlow()
      ownMaterials.add(glow.material)
      glow.position.copy(mesh.position)
      glow.scale.set(0.16, 0.22, 1)
      glow.name = `${mesh.name}_Bioluminescence`
      mesh.parent!.add(glow)
      undo.push(() => { mesh.visible = visible; glow.removeFromParent(); glow.material.map?.dispose() })
    }
    mesh.material = Array.isArray(original) ? original.map(materialFor) : materialFor(original)
    undo.push(() => { mesh.material = original })

    if (character === 'bluebob' && /^BB_Hand_[LR]$/.test(mesh.name)) {
      replaceGeometry(mesh, new RoundedBoxGeometry(1.65, 1.55, 1.25, 4, 0.5))
      const side = mesh.name.endsWith('_L') ? 1 : -1
      for (let finger = 0; finger < 4; finger++) {
        const geometry = new THREE.CapsuleGeometry(0.25, 0.5 - finger * 0.035, 6, 10)
        ownGeometries.add(geometry)
        const knuckle = new THREE.Mesh(geometry, mesh.material)
        knuckle.name = `ModeledFinger_${finger}`
        knuckle.position.set((finger - 1.5) * 0.39, -0.18, 0.47)
        knuckle.rotation.z = (finger - 1.5) * -0.05
        mesh.add(knuckle)
        undo.push(() => knuckle.removeFromParent())
      }
      const geometry = new THREE.SphereGeometry(1, 16, 12)
      ownGeometries.add(geometry)
      const thumb = new THREE.Mesh(geometry, mesh.material)
      thumb.name = 'ModeledThumb'
      thumb.scale.set(0.4, 0.57, 0.37)
      thumb.position.set(side * 0.7, 0.12, 0.5)
      thumb.rotation.z = side * 0.45
      mesh.add(thumb)
      undo.push(() => thumb.removeFromParent())
    }
    if (character === 'bluebob' && mesh.name === 'BB_Collar' && mesh.userData.authoredSurface) {
      replaceGeometry(mesh, makeClothWrap(mesh, false))
      for (const side of [-1, 1]) {
        const shape = new THREE.Shape()
        shape.moveTo(side * 0.04, 0.28)
        shape.lineTo(side * 0.43, 0.16)
        shape.lineTo(side * 0.24, -0.65)
        shape.closePath()
        const geometry = new THREE.ExtrudeGeometry(shape, { depth: 0.06, bevelEnabled: true, bevelSize: 0.035, bevelThickness: 0.025, bevelSegments: 2, steps: 1 })
        ownGeometries.add(geometry)
        const panel = new THREE.Mesh(geometry, mesh.material)
        panel.name = `TailoredCollar_${side}`
        panel.position.z = 0.35
        mesh.add(panel)
        undo.push(() => panel.removeFromParent())
      }
    }
    if (/^BB_Iris_[LR]$/.test(mesh.name)) {
      const geometry = new THREE.SphereGeometry(1, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2)
      geometry.rotateX(Math.PI / 2)
      ownGeometries.add(geometry)
      const corneaMaterial = new THREE.MeshPhysicalMaterial({ color: 0xe5ffff, transparent: true, opacity: 0.035, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, depthWrite: false })
      ownMaterials.add(corneaMaterial)
      const cornea = new THREE.Mesh(geometry, corneaMaterial)
      cornea.name = 'CornealLens'
      cornea.scale.set(0.99, 0.99, 0.31)
      cornea.position.z = 0.94
      mesh.add(cornea)
      undo.push(() => cornea.removeFromParent())
    }

    if (character === 'bluebob' && mesh.name === 'BB_Pants') {
      const thread = new THREE.MeshStandardMaterial({ color: 0x95785b, roughness: 0.96 })
      ownMaterials.add(thread)
      for (const side of [-1, 1]) {
        const points = [[side * 0.16,0.4,0.35],[side * 0.19,0.29,0.357],
          [side * 0.29,0.255,0.36],[side * 0.41,0.3,0.35],[side * 0.43,0.4,0.342]]
          .map(([x,y,z]) => new THREE.Vector3(x,y,z).sub(mesh.position).divide(mesh.scale))
        const geometry = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 24, 0.006, 4, false)
        ownGeometries.add(geometry)
        const seam = new THREE.Mesh(geometry, thread)
        seam.name = `BB_PocketSeam_${side}`
        seam.castShadow = true
        mesh.add(seam)
        undo.push(() => seam.removeFromParent())
      }
    }

    if (/^BB_Shoe_[LR]$/.test(mesh.name)) replaceGeometry(mesh, makeShoeUpper())
    if (mesh.name === 'BB_TieKnot' || mesh.name === 'BB_Tie') {
      const geometry = mesh.geometry.clone()
      geometry.translate(0, mesh.name === 'BB_TieKnot' ? -0.55 : 0.22, 0)
      replaceGeometry(mesh, geometry)
    }
    if (mesh.name === 'MK_Shirt') replaceGeometry(mesh, makeClothWrap(mesh, true))
    if (/^MK_Stalk_[LR]$/.test(mesh.name)) {
      const geometry = new THREE.CylinderGeometry(1, 1, 2, 20, 16)
      const position = geometry.attributes.position
      for (let i = 0; i < position.count; i++) {
        const y = position.getY(i), t = (y + 1) * 0.5
        const width = 0.68 + 0.36 * Math.pow(Math.abs(y), 5)
        position.setXYZ(i, position.getX(i) * width + 0.26 * Math.sin(t * Math.PI), y, position.getZ(i) * width)
      }
      geometry.computeVertexNormals()
      replaceGeometry(mesh, geometry)
    }

    if (/^MK_Arm_[LR]$/.test(mesh.name)) {
      const profile = [[0,-1.02],[0.82,-1],[1.15,-0.8],[1.2,-0.62],[0.86,-0.5],[0.78,-0.22],[0.88,0.04],[1.22,0.08],[1.24,0.22],[1.03,0.55],[0.85,0.94],[0,1.01]].map(([x,y])=>new THREE.Vector2(x,y))
      replaceGeometry(mesh, new THREE.LatheGeometry(profile, 28))
    }
    if (/^MK_Pinc[UL]_/.test(mesh.name) || /^(BB_Pupil_[LR]|MK_Glint_[LR]|ZP_Glint_[LR]|JK_Glint_[LR])$/.test(mesh.name)) {
      const visible = mesh.visible
      mesh.visible = false
      undo.push(() => { mesh.visible = visible })
    }
    if (/^MK_ClawBase_[LR]$/.test(mesh.name)) {
      const claw = new THREE.Shape()
      claw.moveTo(-0.62, -0.74)
      claw.bezierCurveTo(-1.2, -0.12, -1.02, 0.95, -0.71, 1.31)
      claw.bezierCurveTo(-0.62, 1.43, -0.45, 1.4, -0.51, 1.2)
      claw.bezierCurveTo(-0.65, 0.73, -0.49, 0.3, -0.22, 0.14)
      claw.quadraticCurveTo(-0.06, 0.04, 0.07, 0.24)
      claw.bezierCurveTo(0.28, 0.56, 0.25, 0.86, 0.28, 1.0)
      claw.bezierCurveTo(0.31, 1.14, 0.41, 1.12, 0.48, 1.0)
      claw.bezierCurveTo(0.97, 0.42, 1.01, -0.31, 0.53, -0.79)
      claw.quadraticCurveTo(0, -1.02, -0.62, -0.74)
      const geometry = new THREE.ExtrudeGeometry(claw, { depth: 0.72, bevelEnabled: true, bevelSize: 0.18, bevelThickness: 0.25, bevelSegments: 5, curveSegments: 20, steps: 1 })
      geometry.translate(0, 0, -0.36)
      const dense = subdivideCharacterGeometry(geometry)
      geometry.dispose()
      const position = dense.attributes.position
      for (let i = 0; i < position.count; i++) {
        const x = position.getX(i), y = position.getY(i), z = position.getZ(i)
        const bulge = 0.38 * Math.max(0, 1 - x * x * 0.7) * Math.max(0, 1 - y * y * 0.65)
        position.setZ(i, z + Math.sign(z) * bulge)
      }
      dense.deleteAttribute('normal')
      dense.deleteAttribute('uv')
      const rounded = mergeVertices(dense, 0.001)
      dense.dispose()
      rounded.computeVertexNormals()
      const uv = new Float32Array(rounded.attributes.position.count * 2)
      for (let i = 0; i < rounded.attributes.position.count; i++) {
        uv[i * 2] = rounded.attributes.position.getX(i) * 0.5 + 0.5
        uv[i * 2 + 1] = rounded.attributes.position.getY(i) * 0.5 + 0.5
      }
      rounded.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
      replaceGeometry(mesh, rounded)
    }
    if (mesh.name === 'MK_Coin') {
      const profile = [[0,-1],[0.88,-1],[0.99,-0.7],[1,-0.45],[1,0.45],[0.99,0.7],[0.88,1],[0,1]].map(([x,y]) => new THREE.Vector2(x,y))
      replaceGeometry(mesh, new THREE.LatheGeometry(profile, 64))
      const geometry = new THREE.TorusGeometry(0.76, 0.045, 8, 48)
      geometry.rotateX(Math.PI / 2)
      geometry.translate(0, -1.04, 0)
      ownGeometries.add(geometry)
      const edging = new THREE.Mesh(geometry, mesh.material)
      edging.name = 'CoinMintedRim'
      mesh.add(edging)
      undo.push(() => edging.removeFromParent())
    }

    if (/^(ZP|JK)_Bell$/.test(mesh.name)) {
      const geometry = mesh.geometry.clone()
      ownGeometries.add(geometry)
      const material = new THREE.MeshStandardMaterial({ color: character === 'king' ? 0x6d438d : 0x238d9b, roughness: 0.7, transparent: false, opacity: 1, depthWrite: true })
      ownMaterials.add(material)
      const tissue = new THREE.Mesh(geometry, material)
      tissue.name = 'InternalBellTissue'
      tissue.scale.set(0.62, 0.65, 0.62)
      tissue.position.y = -0.1
      tissue.renderOrder = -1
      mesh.add(tissue)
      undo.push(() => tissue.removeFromParent())
    }

    if (/^(ZP|JK)_Rim$/.test(mesh.name)) {
      const gel = new THREE.MeshPhysicalMaterial({ color: character === 'king' ? 0x9a69c1 : 0x31b8c5 })
      applyJellyMembrane(gel)
      gel.thickness = 0.055
      gel.roughness = 0.2
      ownMaterials.add(gel)
      mesh.material = gel

      const geometry = new THREE.TorusGeometry(0.96, 0.048, 16, 192)
      geometry.rotateX(Math.PI / 2)
      const position = geometry.attributes.position
      for (let i = 0; i < position.count; i++) {
        const x = position.getX(i), z = position.getZ(i), angle = Math.atan2(z, x)
        const fold = Math.cos(angle * 12)
        const r = Math.hypot(x, z)
        position.setXYZ(i, x * (1 + 0.02 * fold / r), position.getY(i) * (0.15 / 0.048) + 0.12 * fold + 0.6, z * (1 + 0.02 * fold / r))
      }
      geometry.computeVertexNormals()
      replaceGeometry(mesh, geometry)
    }

    if (character === 'king' && mesh.name === 'JK_CrownBand') {
      replaceGeometry(mesh, makeCrownRibbon())
      const geometry = new THREE.TorusGeometry(0.975, 0.045, 10, 96)
      geometry.rotateX(Math.PI / 2)
      geometry.scale(1, 2.4, 1)
      geometry.translate(0, 0.45, 0)
      ownGeometries.add(geometry)
      const band = new THREE.Mesh(geometry, mesh.material)
      band.name = 'CrownRolledLowerBand'
      mesh.add(band)
      undo.push(() => band.removeFromParent())

    }
    if (mesh.name === 'JK_CrownJewel') {
      const geometry = new THREE.TorusGeometry(1, 0.14, 10, 40)
      geometry.translate(0, 0, 0.16)
      const material = new THREE.MeshStandardMaterial({ color: 0xe9b448, metalness: 0.92, roughness: 0.28 })
      ownGeometries.add(geometry); ownMaterials.add(material)
      const bezel = new THREE.Mesh(geometry, material)
      bezel.name = 'CrownJewelBezel'
      mesh.add(bezel)
      undo.push(() => bezel.removeFromParent())
    }
    if (character === 'king' && mesh.name.startsWith('JK_Spike_')) {
      const visible = mesh.visible
      mesh.visible = false
      undo.push(() => { mesh.visible = visible })
    }

    if (character === 'bluebob' && mesh.name === 'BlueBob_Body' && !mesh.userData.authoredSurface) {
      mesh.geometry.computeBoundingBox()
      const bounds = mesh.geometry.boundingBox!
      const size = bounds.getSize(new THREE.Vector3())
      const center = bounds.getCenter(new THREE.Vector3())
      const rounded = new RoundedBoxGeometry(size.x, size.y, size.z, 5, Math.min(size.x, size.y, size.z) * 0.16)
      rounded.translate(center.x, center.y, center.z)
      replaceGeometry(mesh, rounded)
    }
    if (/^(BB_Smile|MK_Mouth|JK_Mouth|ZP_Mouth)$/.test(mesh.name)) {
      // Authored crab mouth sat behind his shirt; rounded bells also occluded
      // their mouth corners. Keep the cutscene aperture in front of the face.
      const oldY = mesh.position.y
      if (character === 'krabs') mesh.position.y = 1.32
      undo.push(() => { mesh.position.y = oldY })
      const oldZ = mesh.position.z
      const facePlane = { bluebob: model.getObjectByName('BlueBob_Body')?.userData.authoredSurface ? 0.295 : oldZ, krabs: model.getObjectByName('MK_Body')?.userData.authoredSurface ? 0.55 : 0.64, king: 0.785, zap: 0.535 }[character]
      mesh.position.z = character === 'bluebob' && model.getObjectByName('BlueBob_Body')?.userData.authoredSurface ? facePlane : Math.max(oldZ, facePlane)
      undo.push(() => { mesh.position.z = oldZ })
      // Keep the original mesh/name/transform so the established viseme scaling
      // drives the curved cavity and its raised lip together.
      const shape = new THREE.Shape()
      shape.moveTo(-1, 0.65)
      shape.quadraticCurveTo(0, 0.4, 1, 0.65)
      shape.bezierCurveTo(0.9, -0.3, 0.55, -0.92, 0, -1)
      shape.bezierCurveTo(-0.55, -0.92, -0.9, -0.3, -1, 0.65)
      const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: 0.65, bevelEnabled: true, bevelSegments: 2, steps: 1,
        bevelSize: 0.045, bevelThickness: 0.09, curveSegments: 12,
      })
      if (character === 'bluebob') geometry.scale(1, 1.35, 1)
      geometry.translate(0, 0, 0.15)
      replaceGeometry(mesh, geometry)
      const cavity = new THREE.MeshStandardMaterial({ color: 0x172230, roughness: 0.48 })
      ownMaterials.add(cavity)
      mesh.material = cavity
      const lipMaterial = new THREE.MeshStandardMaterial({
        color: character === 'bluebob' ? 0x3d86a3 : character === 'krabs' ? 0x80372d : 0x675c93,
        roughness: 0.43,
      })
      ownMaterials.add(lipMaterial)
      const edge = shape.getPoints(28).map((p) => new THREE.Vector3(p.x, p.y * (character === 'bluebob' ? 1.35 : 1), 0.77))
      const lipGeometry = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(edge, true), 64, 0.085, 8, true)
      ownGeometries.add(lipGeometry)
      const lip = new THREE.Mesh(lipGeometry, lipMaterial)
      lip.name = 'DimensionalLip'
      lip.castShadow = true
      mesh.add(lip)
      undo.push(() => lip.removeFromParent())
    }
    if (/^(BB|MK)_Tooth/.test(mesh.name)) {
      const mouth = model.getObjectByName(character === 'krabs' ? 'MK_Mouth' : 'BB_Smile')
      if (mouth) {
        const original = mesh.position.clone()
        const facePlane = mouth.position.z
        mesh.position.z = facePlane + 0.025
        mesh.position.y = (character === 'krabs' ? 1.32 : mouth.position.y) - 0.026
        const originalScale = mesh.scale.clone()
        mesh.scale.y *= character === 'bluebob' ? 0.68 : 0.58
        if (character === 'bluebob') mesh.scale.x *= 0.86
        undo.push(() => { mesh.position.copy(original); mesh.scale.copy(originalScale) })
      }
    }
    if (mesh.name.startsWith('BB_Tooth')) {
      replaceGeometry(mesh, new RoundedBoxGeometry(2, 2, 2, 3, 0.3))
    }
    if (mesh.name.startsWith('BB_Hole_')) {
      if (model.getObjectByName('BlueBob_Body')?.userData.authoredSurface) {
        const visible = mesh.visible
        mesh.visible = false
        undo.push(() => { mesh.visible = visible })
        continue
      }
      // Existing GLB "pores" were protruding marbles. A shallow concave cup has
      // a genuine rim and recessed center that catch the miniature key light.
      const profile = [[0,-0.2],[0.25,-0.2],[0.55,-0.15],[0.78,-0.04],[0.92,0.015],[1,-0.03]]
        .map(([r,h]) => new THREE.Vector2(r,h))
      const geometry = new THREE.LatheGeometry(profile, 16)
      geometry.rotateX(Math.PI / 2)
      replaceGeometry(mesh, geometry)
      const originalZ = mesh.position.z
      mesh.position.z = 0.325
      undo.push(() => { mesh.position.z = originalZ })
    }
  }

  const disposeTentacles = installTentacleSurface(model)
  let disposed = false
  return () => {
    if (disposed) return
    disposed = true
    disposeTentacles()
    for (let i = undo.length - 1; i >= 0; i--) undo[i]()
    ownGeometries.forEach((geometry) => geometry.dispose())
    ownMaterials.forEach((material) => material.dispose())
    poreMap?.dispose()
    surfaceMaps.forEach(texture => texture.dispose())
  }
}

function makePoreTexture(): THREE.DataTexture {
  const size = 256
  const heights = new Float32Array(size * size).fill(0.72)
  let seed = 17491
  const random = () => {
    seed = Math.imul(seed, 1664525) + 1013904223 | 0
    return (seed >>> 0) / 4294967296
  }
  for (let i = 0; i < 520; i++) {
    const x = random() * size, y = random() * size
    const radius = 1.2 + random() * 3.6
    for (let dy = -6; dy <= 6; dy++) for (let dx = -6; dx <= 6; dx++) {
      const distance = Math.hypot(dx, dy) / radius
      if (distance > 1.2) continue
      const px = (Math.floor(x) + dx + size) % size
      const py = (Math.floor(y) + dy + size) % size
      const bowl = Math.max(0, 1 - distance * distance)
      const rim = Math.exp(-(((distance - 0.95) / 0.14) ** 2)) * 0.07
      heights[py * size + px] = Math.min(heights[py * size + px], 0.72 - bowl * 0.48 + rim)
    }
  }
  const data = new Uint8Array(size * size * 4)
  for (let i = 0; i < heights.length; i++) {
    const value = Math.round(Math.max(0, Math.min(1, heights[i] + (random() - 0.5) * 0.045)) * 255)
    data[i * 4] = data[i * 4 + 1] = data[i * 4 + 2] = value
    data[i * 4 + 3] = 255
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat)
  texture.name = 'CutsceneSpongeMicroPores'
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping
  texture.repeat.set(2, 2)
  texture.magFilter = THREE.LinearFilter
  texture.minFilter = THREE.LinearMipmapLinearFilter
  texture.generateMipmaps = true
  texture.needsUpdate = true
  return texture
}

function makeSparkGlow(): THREE.Sprite {
  const size = 32, data = new Uint8Array(size * size * 4)
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const radius = Math.hypot((x + 0.5) / size * 2 - 1, (y + 0.5) / size * 2 - 1)
    const i = (y * size + x) * 4
    data[i] = 255; data[i + 1] = 232; data[i + 2] = 115
    data[i + 3] = Math.round(Math.exp(-radius * radius * 6) * Math.max(0, 1 - radius) * 255)
  }
  const map = new THREE.DataTexture(data, size, size)
  map.needsUpdate = true
  return new THREE.Sprite(new THREE.SpriteMaterial({ map, color: 0xffe7ad, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }))
}

/** Continuous sheet-metal crown with eight pointed peaks and rolled beveled edges.
 * Coordinates retain the authored band's origin/scale and the original crown height. */
function makeCrownRibbon(): THREE.BufferGeometry {
  const positions: number[] = [], uvs: number[] = [], indices: number[] = []
  const segments = 96, profileCount = 8
  for (let i = 0; i < segments; i++) {
    const angle = i / segments * Math.PI * 2
    const phase = ((i / segments * 8 + 0.5) % 1) * 2 - 1
    const top = 1 + (1 - Math.abs(phase)) * 2.66
    const profile = [[0.97,-0.8],[1.02,-0.68],[1.02,top-0.12],[0.98,top],
      [0.9,top],[0.86,top-0.12],[0.86,-0.68],[0.9,-0.8]]
    for (let j = 0; j < profileCount; j++) {
      const [radius, height] = profile[j]
      positions.push(Math.cos(angle) * radius, height, Math.sin(angle) * radius)
      uvs.push(i / segments, j / profileCount)
      const a = i * profileCount + j
      const b = i * profileCount + (j + 1) % profileCount
      const c = ((i + 1) % segments) * profileCount + j
      const d = ((i + 1) % segments) * profileCount + (j + 1) % profileCount
      indices.push(a, b, c, b, d, c)
    }
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  geometry.setIndex(indices)
  geometry.computeVertexNormals()
  return geometry
}

function makeClothWrap(mesh: THREE.Mesh, crab: boolean): THREE.BufferGeometry {
  const vertices: number[] = [], uv: number[] = [], indices: number[] = []
  const rings = 12, sides = 96
  for (let j = 0; j <= rings; j++) for (let i = 0; i <= sides; i++) {
    const t = j / rings, a = i / sides * Math.PI * 2
    const sx = Math.cos(a), sz = Math.sin(a)
    let x = crab ? sx * (0.675 + 0.06 * t) : Math.sign(sx) * Math.pow(Math.abs(sx), 0.36) * (0.495 + 0.02 * t)
    let z = crab ? sz * (0.47 + 0.075 * t) : Math.sign(sz) * Math.pow(Math.abs(sz), 0.36) * 0.32
    const top = crab ? 1.04 + 0.12 * Math.abs(sx) - 0.13 * Math.pow(Math.max(0, sz), 10) : 0.63 + 0.01 * Math.cos(a * 3)
    const bottom = crab ? 0.59 : 0.505
    if (crab) { const y = THREE.MathUtils.lerp(bottom, top, t); const section = Math.sqrt(Math.max(0, 1 - Math.pow((y - 1) / 0.62, 2))); x = sx * Math.max(0.675, 0.75 * section + 0.035); z = sz * Math.max(0.49, 0.55 * section + 0.035) }
    let fold = Math.sin(a * 17 + t * 1.5) * Math.sin(t * Math.PI) * (crab ? 0.012 : 0.007)
    if (crab) { fold += 0.013 * Math.exp(-Math.pow(t / 0.085, 2)); for (const seam of [0.92, 2.22]) fold -= 0.016 * Math.exp(-Math.pow((a - seam) / 0.05, 2)) }
    const point = new THREE.Vector3(x + sx * fold, THREE.MathUtils.lerp(bottom, top, t), z + sz * fold).sub(mesh.position).divide(mesh.scale)
    vertices.push(point.x, point.y, point.z); uv.push(i / sides, t)
    if (j < rings && i < sides) { const n = j * (sides + 1) + i; indices.push(n, n + sides + 1, n + 1, n + 1, n + sides + 1, n + sides + 2) }
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));geometry.setIndex(indices);geometry.computeVertexNormals()
  return geometry
}

function makeShoeUpper(): THREE.BufferGeometry {
  const positions: number[] = [], uv: number[] = [], indices: number[] = []
  const profile = [[-1,0.88],[-0.76,1],[-0.55,1],[-0.38,0.96],[0.0,0.90],[0.4,0.76],[0.8,0.5],[1.15,0.28],[1.18,0.02]]
  const sides = 48
  for (let j = 0; j < profile.length; j++) for (let i = 0; i <= sides; i++) {
    const a = i / sides * Math.PI * 2, [height,r] = profile[j]
    const front = Math.max(0, Math.sin(a))
    positions.push(Math.cos(a) * r, height - Math.max(0,height + 0.2) * front * 0.65, Math.sin(a) * r - Math.max(0,height) * 0.27)
    uv.push(i / sides, j / (profile.length - 1))
    if (j < profile.length - 1 && i < sides) { const n = j * (sides + 1) + i; indices.push(n,n + sides + 1,n + 1,n + 1,n + sides + 1,n + sides + 2) }
  }
  const geometry = new THREE.BufferGeometry();geometry.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));geometry.setAttribute('uv',new THREE.Float32BufferAttribute(uv,2));geometry.setIndex(indices);geometry.computeVertexNormals();return geometry
}

/** Uniform edge subdivision prevents cracks when adjacent faces are sculpted. */
function subdivideCharacterGeometry(source: THREE.BufferGeometry): THREE.BufferGeometry {
  const flat = source.index ? source.toNonIndexed() : source.clone()
  const p = flat.attributes.position, vertices: number[] = []
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3()
  for (let i = 0; i < p.count; i += 3) {
    a.fromBufferAttribute(p, i); b.fromBufferAttribute(p, i + 1); c.fromBufferAttribute(p, i + 2)
    const ab = a.clone().add(b).multiplyScalar(0.5), bc = b.clone().add(c).multiplyScalar(0.5), ca = c.clone().add(a).multiplyScalar(0.5)
    for (const v of [a,ab,ca,ab,b,bc,ca,bc,c,ab,bc,ca]) vertices.push(v.x,v.y,v.z)
  }
  flat.dispose()
  const result = new THREE.BufferGeometry(); result.setAttribute('position',new THREE.Float32BufferAttribute(vertices,3)); return result
}
